import {
  ClipboardDocumentListIcon,
  StarIcon,
  HeartIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { getSupabaseClient } from "../lib/supabase";

type Line = {
  qty: string;
  price: string;
  sessionLengthHours: string;
  sessionLengthMinutes: string;
};

// Helper to get total minutes from hours and minutes
const getTotalMinutes = (hours: number, minutes: number): number => {
  return (hours * 60) + Math.min(59, Math.max(0, minutes));
};

type AdditionalComponent = {
  id: string;
  name: string;
  qty: string;
  price: string;
  sessionLengthHours: string;
  sessionLengthMinutes: string;
};

export default function PackageBuilder() {
  const navigate = useNavigate();

  // Header form
  const [pkgName, setPkgName] = useState('');
  const [duration, setDuration] = useState<number>(3);
  const [desc, setDesc] = useState('');

  // 3 fixed blocks
  const [prep, setPrep] = useState<Line>({ qty: '2', price: '150', sessionLengthHours: '1', sessionLengthMinutes: '0' });
  const [ceremony, setCeremony] = useState<Line>({ qty: '4', price: '400', sessionLengthHours: '1', sessionLengthMinutes: '0' });
  const [integration, setIntegration] = useState<Line>({ qty: '6', price: '125', sessionLengthHours: '1', sessionLengthMinutes: '0' });

  // additional components (array)
  const [additionalComponents, setAdditionalComponents] = useState<AdditionalComponent[]>([]);

  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const supabase = getSupabaseClient();

  const addAdditionalComponent = () => {
    const newComponent: AdditionalComponent = {
      id: Date.now().toString(),
      name: '',
      qty: '1',
      price: '0',
      sessionLengthHours: '1',
      sessionLengthMinutes: '0'
    };
    setAdditionalComponents([...additionalComponents, newComponent]);
  };

  const updateAdditionalComponent = (index: number, field: keyof AdditionalComponent, value: any) => {
    const updated = [...additionalComponents];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalComponents(updated);
  };

  const removeAdditionalComponent = (index: number) => {
    setAdditionalComponents(additionalComponents.filter((_, i) => i !== index));
  };

  const subtotal = (l: Line) => (Number(l.qty) || 0) * (Number(l.price) || 0);
  const additionalSubtotal = additionalComponents.reduce((sum, comp) =>
    sum + (Number(comp.qty) || 0) * (Number(comp.price) || 0), 0
  );
  const total = subtotal(prep) + subtotal(ceremony) + subtotal(integration) + additionalSubtotal;

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!pkgName.trim()) {
      errs.pkgName = 'Package name is required';
    }
    const totalQty = (Number(prep.qty) || 0) + (Number(ceremony.qty) || 0) + (Number(integration.qty) || 0) +
      additionalComponents.reduce((sum, comp) => sum + (Number(comp.qty) || 0), 0);
    if (totalQty <= 0) {
      errs.qty = 'At least one component must have sessions';
    }
    additionalComponents.forEach((comp, i) => {
      if ((Number(comp.qty) || 0) > 0 && !comp.name.trim()) {
        errs[`additional_${i}`] = `Additional service #${i + 1} needs a name`;
      }
    });
    return errs;
  }

  const validationErrors = validate();
  const hasErrors = Object.keys(validationErrors).length > 0;

  async function saveAndContinue() {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const { data: { user }, error: uerr } = await supabase.auth.getUser();
      if (uerr) throw uerr;
      if (!user) { alert('Not logged in'); return; }

      // 1) insert header
      const { data: header, error: herr } = await supabase
        .from('guide_packages')
        .insert({
          guide_id: user.id,
          name: pkgName.trim(),
          duration_months: duration,
          description: desc.trim() || null,
          is_active: true,
        })
        .select('id')
        .single();
      if (herr) throw herr;

      const pkgId = header.id as string;

      // 2) prepare lines
      const lines: Array<any> = [
        {
          package_id: pkgId,
          component_type: 'preparation',
          name: 'Preparation Sessions',
          quantity: Math.max(0, Number(prep.qty) || 0),
          unit_price_dollars: Math.max(0, Number(prep.price) || 0),
          session_length_minutes: Math.max(1, getTotalMinutes(Number(prep.sessionLengthHours) || 0, Number(prep.sessionLengthMinutes) || 0)),
          sort_order: 10,
        },
        {
          package_id: pkgId,
          component_type: 'ceremony',
          name: 'Core Ceremony Sessions',
          quantity: Math.max(0, Number(ceremony.qty) || 0),
          unit_price_dollars: Math.max(0, Number(ceremony.price) || 0),
          session_length_minutes: Math.max(1, getTotalMinutes(Number(ceremony.sessionLengthHours) || 0, Number(ceremony.sessionLengthMinutes) || 0)),
          sort_order: 20,
        },
        {
          package_id: pkgId,
          component_type: 'integration',
          name: 'Integration Sessions',
          quantity: Math.max(0, Number(integration.qty) || 0),
          unit_price_dollars: Math.max(0, Number(integration.price) || 0),
          session_length_minutes: Math.max(1, getTotalMinutes(Number(integration.sessionLengthHours) || 0, Number(integration.sessionLengthMinutes) || 0)),
          sort_order: 30,
        },
      ];

      // Add additional components
      additionalComponents.forEach((comp, index) => {
        if (comp.name.trim() && (Number(comp.qty) || 0) > 0) {
          lines.push({
            package_id: pkgId,
            component_type: 'additional',
            name: comp.name.trim(),
            quantity: Math.max(0, Number(comp.qty) || 0),
            unit_price_dollars: Math.max(0, Number(comp.price) || 0),
            session_length_minutes: Math.max(1, getTotalMinutes(Number(comp.sessionLengthHours) || 0, Number(comp.sessionLengthMinutes) || 0)),
            sort_order: 40 + index,
          });
        }
      });

      // 3) insert lines
      if (lines.length) {
        const { error: lerr } = await supabase
          .from('guide_package_components')
          .insert(lines);
        if (lerr) throw lerr;
      }

      navigate('/onboarding/complete-profile');
    } catch (e: any) {
      console.error(e);
      alert(e?.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#fffcef] px-4 py-10">
      <div className="mx-auto max-w-4xl text-[#304031]">
        {/* Title */}
        <h1 className="mb-3 text-center font-recoleta text-[32px] sm:text-[42px] leading-[36px] sm:leading-[44px] font-medium text-[#304031]">
          Your Standard Package
        </h1>
        <p className="mb-8 sm:mb-12 text-center text-base sm:text-lg text-[#304031]/70 px-2">
          Define your standard offering to help us understand your service structure and pricing.
        </p>

      {/* Package Overview */}
      <div className="bg-[#fffcef] border-2 border-[#a0978a] rounded-2xl p-4 sm:p-8 shadow-sm mb-8">
        <h2 className="text-xl font-semibold text-[#304031] mb-6">Package Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-[#304031] mb-2">Package Name</label>
            <input
              type="text"
              placeholder="e.g., 3-Month Transformation Journey"
              value={pkgName}
              onChange={(e) => {
                setPkgName(e.target.value);
                if (errors.pkgName) setErrors((prev) => { const { pkgName: _, ...rest } = prev; return rest; });
              }}
              className={`border-2 ${errors.pkgName ? 'border-red-400' : 'border-[#a0978a]'} rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20`}
            />
            {errors.pkgName && (
              <p className="mt-1 text-sm text-red-600">{errors.pkgName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#304031] mb-2">Package Duration</label>
            <select
              className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              <option value={3}>3 Months</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#304031] mb-2">Package Description</label>
          <textarea
            rows={4}
            placeholder="Describe what this package includes and the transformation clients can expect..."
            value={desc}
            onChange={(e) => setDesc(e.target.value.slice(0, 500))}
            maxLength={500}
            className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
          />
          <p className={`mt-1 text-right text-xs ${desc.length >= 450 ? 'text-red-500' : 'text-[#304031]/50'}`}>
            {desc.length}/500
          </p>
        </div>
      </div>

      {/* Service Components */}
      <div className="bg-[#fffcef] border-2 border-[#a0978a] rounded-2xl p-4 sm:p-8 shadow-sm mb-8">
        <h2 className="text-xl font-semibold text-[#304031] mb-6">Service Components</h2>

        {/* Preparation Sessions */}
        <div className="bg-[#304031]/5 rounded-xl p-4 sm:p-6 mb-6 border-2 border-[#a0978a] overflow-hidden">
          <p className="font-semibold text-[#304031] mb-4 flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-5 w-5 text-[#304031]" /> Preparation Sessions
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Sessions</label>
              <input
                type="number"
                value={prep.qty}
                onChange={(e) => {
                  setPrep(prev => ({ ...prev, qty: e.target.value }));
                  if (errors.general) setErrors((prev) => { const { general: _, ...rest } = prev; return rest; });
                }}
                className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                min="0"
              />
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Length</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={prep.sessionLengthHours}
                  onChange={(e) => setPrep(prev => ({ ...prev, sessionLengthHours: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                  min="0"
                />
                <span className="text-[#304031]/70 text-xs">h</span>
                <input
                  type="number"
                  value={prep.sessionLengthMinutes}
                  onChange={(e) => setPrep(prev => ({ ...prev, sessionLengthMinutes: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                  min="0"
                  max="59"
                />
                <span className="text-[#304031]/70 text-xs">m</span>
              </div>
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Price</label>
              <div className="flex items-center gap-1">
                <span className="text-[#304031]">$</span>
                <input
                  type="number"
                  value={prep.price}
                  onChange={(e) => setPrep(prev => ({ ...prev, price: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                  min="0"
                />
              </div>
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Subtotal</label>
              <input
                type="text"
                value={`$${subtotal(prep)}`}
                readOnly
                className="bg-[#304031]/10 border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full text-[#304031] font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Core Ceremony Sessions */}
        <div className="bg-[#304031]/5 rounded-xl p-4 sm:p-6 mb-6 border-2 border-[#a0978a] overflow-hidden">
          <p className="font-semibold text-[#304031] mb-4 flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-[#304031]" /> Core Ceremony Sessions
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Sessions</label>
              <input
                type="number"
                value={ceremony.qty}
                onChange={(e) => {
                  setCeremony(prev => ({ ...prev, qty: e.target.value }));
                  if (errors.general) setErrors((prev) => { const { general: _, ...rest } = prev; return rest; });
                }}
                className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                min="0"
              />
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Length</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={ceremony.sessionLengthHours}
                  onChange={(e) => setCeremony(prev => ({ ...prev, sessionLengthHours: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                  min="0"
                />
                <span className="text-[#304031]/70 text-xs">h</span>
                <input
                  type="number"
                  value={ceremony.sessionLengthMinutes}
                  onChange={(e) => setCeremony(prev => ({ ...prev, sessionLengthMinutes: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                  min="0"
                  max="59"
                />
                <span className="text-[#304031]/70 text-xs">m</span>
              </div>
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Price</label>
              <div className="flex items-center gap-1">
                <span className="text-[#304031]">$</span>
                <input
                  type="number"
                  value={ceremony.price}
                  onChange={(e) => setCeremony(prev => ({ ...prev, price: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                  min="0"
                />
              </div>
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Subtotal</label>
              <input
                type="text"
                value={`$${subtotal(ceremony)}`}
                readOnly
                className="bg-[#304031]/10 border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full text-[#304031] font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Integration Sessions */}
        <div className="bg-[#304031]/5 rounded-xl p-4 sm:p-6 mb-6 border-2 border-[#a0978a] overflow-hidden">
          <p className="font-semibold text-[#304031] mb-4 flex items-center gap-2">
            <HeartIcon className="h-5 w-5 text-[#304031]" /> Integration Sessions
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Sessions</label>
              <input
                type="number"
                value={integration.qty}
                onChange={(e) => {
                  setIntegration(prev => ({ ...prev, qty: e.target.value }));
                  if (errors.general) setErrors((prev) => { const { general: _, ...rest } = prev; return rest; });
                }}
                className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                min="0"
              />
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Length</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={integration.sessionLengthHours}
                  onChange={(e) => setIntegration(prev => ({ ...prev, sessionLengthHours: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                  min="0"
                />
                <span className="text-[#304031]/70 text-xs">h</span>
                <input
                  type="number"
                  value={integration.sessionLengthMinutes}
                  onChange={(e) => setIntegration(prev => ({ ...prev, sessionLengthMinutes: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                  min="0"
                  max="59"
                />
                <span className="text-[#304031]/70 text-xs">m</span>
              </div>
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Price</label>
              <div className="flex items-center gap-1">
                <span className="text-[#304031]">$</span>
                <input
                  type="number"
                  value={integration.price}
                  onChange={(e) => setIntegration(prev => ({ ...prev, price: e.target.value }))}
                  className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                  min="0"
                />
              </div>
            </div>
            <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
              <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Subtotal</label>
              <input
                type="text"
                value={`$${subtotal(integration)}`}
                readOnly
                className="bg-[#304031]/10 border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full text-[#304031] font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Additional Services */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold text-[#304031] flex items-center gap-2">
              <PlusIcon className="h-5 w-5 text-[#304031]" /> Additional Services
            </p>
            <button
              onClick={addAdditionalComponent}
              className="inline-flex items-center px-4 py-2 text-sm bg-[#304031] text-[#fef6d6] rounded-full hover:bg-[#304031]/90 transition-colors font-medium"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Service
            </button>
          </div>

          {additionalComponents.map((component, index) => (
            <div key={component.id} className="bg-[#304031]/5 rounded-xl p-4 sm:p-6 mb-4 border-2 border-[#a0978a] overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <input
                  type="text"
                  value={component.name}
                  onChange={(e) => updateAdditionalComponent(index, 'name', e.target.value)}
                  placeholder="e.g., Email Support, Coaching Calls"
                  className="font-semibold text-[#304031] bg-transparent border-none outline-none flex-1 text-base placeholder:text-[#304031]/40"
                />
                <button
                  onClick={() => removeAdditionalComponent(index)}
                  className="ml-2 p-2 text-[#304031]/40 hover:text-red-600 transition-colors rounded-full hover:bg-red-50"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
                <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
                  <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Sessions</label>
                  <input
                    type="number"
                    value={component.qty}
                    onChange={(e) => {
                      updateAdditionalComponent(index, 'qty', e.target.value);
                      if (errors.general) setErrors((prev) => { const { general: _, ...rest } = prev; return rest; });
                    }}
                    className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                    min="0"
                  />
                </div>
                <div className="w-[calc(50%-6px)] sm:w-auto">
                  <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Length</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={component.sessionLengthHours}
                      onChange={(e) => updateAdditionalComponent(index, 'sessionLengthHours', e.target.value)}
                      className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                      min="0"
                    />
                    <span className="text-[#304031]/70 text-xs">h</span>
                    <input
                      type="number"
                      value={component.sessionLengthMinutes}
                      onChange={(e) => updateAdditionalComponent(index, 'sessionLengthMinutes', e.target.value)}
                      className="border-2 border-[#a0978a] rounded-lg px-1 py-2 w-10 sm:w-12 bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20 text-center"
                      min="0"
                      max="59"
                    />
                    <span className="text-[#304031]/70 text-xs">m</span>
                  </div>
                </div>
                <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
                  <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Price</label>
                  <div className="flex items-center gap-1">
                    <span className="text-[#304031]">$</span>
                    <input
                      type="number"
                      value={component.price}
                      onChange={(e) => updateAdditionalComponent(index, 'price', e.target.value)}
                      className="border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full bg-[#fffcef] text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
                      min="0"
                    />
                  </div>
                </div>
                <div className="w-[calc(50%-6px)] sm:w-auto sm:flex-1">
                  <label className="block text-[#304031]/70 font-medium mb-2 text-xs sm:text-sm">Subtotal</label>
                  <input
                    type="text"
                    value={`$${((Number(component.qty) || 0) * (Number(component.price) || 0)).toLocaleString()}`}
                    readOnly
                    className="bg-[#304031]/10 border-2 border-[#a0978a] rounded-lg px-3 py-2 w-full text-[#304031] font-semibold"
                  />
                </div>
              </div>
            </div>
          ))}

          {additionalComponents.length === 0 && (
            <div className="bg-[#304031]/5 rounded-xl p-6 text-center text-sm text-[#304031]/60 border-2 border-[#a0978a]">
              No additional services added yet. Click "Add Service" to create your first additional service.
            </div>
          )}
        </div>
      </div>

      {/* Total Package Cost */}
      <div className="bg-[#304031] text-[#fef6d6] rounded-2xl p-4 sm:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 border-2 border-[#304031]">
        <div>
          <p className="font-semibold text-lg sm:text-xl">Total Package Cost</p>
          <p className="text-sm text-[#fef6d6]/80">This will be your standard {duration}-month offering</p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-2xl sm:text-3xl font-bold">${total.toLocaleString()}</p>
          <p className="text-sm text-[#fef6d6]/80">
            {(Number(prep.qty) || 0) + (Number(ceremony.qty) || 0) + (Number(integration.qty) || 0) + additionalComponents.reduce((sum, comp) => sum + (Number(comp.qty) || 0), 0)} sessions total
          </p>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center sm:justify-between">
        <button
          onClick={() => navigate('/onboarding/complete-profile')}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 border-2 border-[#304031] px-6 py-3 rounded-full hover:bg-[#304031]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-[#304031]"
        >
          ← Back
        </button>
        <div className="flex flex-col items-stretch sm:items-end">
          {hasErrors && (
            <div className="mb-4 rounded-lg border border-[#a0978a] bg-[#304031]/5 p-3 text-sm text-[#304031]/80">
              <p className="font-medium text-[#304031] mb-1">To continue, please fix the following:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {Object.values(validationErrors).map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            onClick={saveAndContinue}
            disabled={busy || hasErrors}
            className={`inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-full font-semibold transition ${
              busy || hasErrors
                ? 'bg-[#304031]/20 text-[#304031]/40 cursor-not-allowed'
                : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
            }`}
          >
            {busy ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-[#fef6d6] border-t-transparent rounded-full"></div>
                Submitting...
              </>
            ) : (
              'Save & Continue →'
            )}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
